import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { differenceInCalendarDays } from 'date-fns';
import { finalize } from 'rxjs';
import { AccordionComponent, ButtonComponent, DropdownSelectComponent } from '@ppl/ui';
import { ButtonModule as PrimeButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { LookupValueItem } from '../../../../../interfaces/lookup.interface';
import { LookupService } from '../../../../../services/lookup.service';
import { UserService } from '../../../../../services/user.service';
import { NgZorroModule } from '../../../../../shared/modules/ng-zorro/ng-zorro.module';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { DateTimeFormatterService } from '../../../../../shared/utils/date-time-formatter.service';
import { PayrollCriterion, TimesheetPayrollProcessingService } from '../timesheet-payroll-processing.service';

const NAVIGATE_BACK_ROUTE = '/home/timesheetPayrollProcessing';

/** Check Date goes to /batch/create in this format, and the picker displays it the same way. */
const CHECK_DATE_FORMAT = 'MM/dd/yyyy';

/**
 * Range operator. Not in the operator lookup domain yet, so it is appended client-side —
 * selecting it reveals a second value input bound to `valueEnd`.
 */
const BETWEEN_OPERATOR_CODE = 'Between';

/** Lookup domains backing the batch form (domains 315, 316, 318 and 319). */
const LOOKUP_KEYS = {
  CRITERIA_COLUMNS: 'Timesheet_Payroll_Batch_Criteria',
  OPERATORS: 'Timesheet_Payroll_Batch_Operator',
  BATCH_TYPES: 'Payroll_Batch_Type',
  DATA_TYPES: 'Payroll_Data_type'
} as const;

/**
 * Check Date cannot be in the past. Compared by calendar day so the time carried on the
 * picker's Date — and today's own time of day — never make an otherwise valid today fail.
 */
function notBeforeTodayValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) {
    return null;
  }

  const selected = value instanceof Date ? value : new Date(value);
  if (isNaN(selected.getTime())) {
    return null;
  }

  return differenceInCalendarDays(selected, new Date()) < 0 ? { pastDate: true } : null;
}

/** Criteria columns whose value is picked from a fixed list instead of typed. */
const FIXED_VALUE_OPTIONS: Record<string, { lkpValueName: string; lkpValueCode: string }[]> = {
  paymentType: [
    { lkpValueName: 'Regular', lkpValueCode: 'Regular' },
    { lkpValueName: 'Adjusted', lkpValueCode: 'Adjusted' },
  ],
};

@Component({
  selector: 'ppl-add-timesheet-payroll-batch',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgZorroModule,
    ButtonComponent,
    AccordionComponent,
    DropdownSelectComponent,
    InputTextModule,
    PrimeButtonModule,
  ],
  templateUrl: './add-timesheet-payroll-batch.component.html',
  styleUrl: './add-timesheet-payroll-batch.component.css',
})
export class AddTimesheetPayrollBatchComponent implements OnInit {
  readonly batchForm = this.fb.group({
    programCode: [{ value: '', disabled: true }],
    batchType: ['', Validators.required],
    dataType: ['', Validators.required],
    checkDate: [null as Date | null, [Validators.required, notBeforeTodayValidator]],
    // Optional: left empty the batch is not narrowed by provider type.
    providerType: [''],
    criteria: this.fb.array<FormGroup>([]),
  });

  /**
   * Blocks past dates in the picker. The validator above still runs, since the picker's
   * input can also be typed into.
   */
  readonly disabledCheckDate = (current: Date): boolean =>
    !!current && differenceInCalendarDays(current, new Date()) < 0;

  isSaving = false;
  programCode = '';

  /**
   * An existing batch opened from the list. Read-only: the contract has no
   * update-batch endpoint, so its criteria cannot be changed after creation.
   */
  isViewMode = false;
  batchInstanceId = '';
  batchRow: any = null;

  criteriaColumns: LookupValueItem[] = [];
  operatorOptions: LookupValueItem[] = [];
  batchTypeOptions: LookupValueItem[] = [];
  dataTypeOptions: LookupValueItem[] = [];
  providerTypeOptions: LookupValueItem[] = [];

  private readonly navigationState: Record<string, any> | undefined;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly notificationService: NotificationService,
    private readonly lookupService: LookupService,
    private readonly payrollService: TimesheetPayrollProcessingService,
    private readonly dateTimeFormatter: DateTimeFormatterService
  ) {
    // Captured here because getCurrentNavigation() returns null once the
    // navigation completes, i.e. by the time ngOnInit runs.
    this.navigationState = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
  }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programCode = selectedProgram?.program_code ?? '';
    this.batchForm.get('programCode')?.setValue(this.programCode);

    // Loaded in both modes — view mode needs them to label stored criteria codes.
    this.initLookups();

    if (this.navigationState?.['batchInstanceId']) {
      this.initViewMode();
      return;
    }

    // Criteria are the only thing scoping a batch, so start with a row ready.
    this.addCriteriaRow();
  }

  private initViewMode(): void {
    const state = this.navigationState ?? {};
    this.isViewMode = true;
    this.batchInstanceId = String(state['batchInstanceId']);
    this.batchRow = state['batchRow'] ?? null;

    const raw = state['editData'];
    const details = raw?.responsedata ?? raw;
    const criteriaList = details?.criteriaList ?? [];

    for (const criterion of criteriaList) {
      this.criteriaFormArray.push(
        this.fb.group({
          columnLkp: [criterion?.columnLkp ?? ''],
          operatorLkp: [criterion?.operatorLkp ?? ''],
          value: [criterion?.value ?? ''],
          valueEnd: [criterion?.valueEnd ?? ''],
        })
      );
    }

    this.batchForm.disable();
  }

  private initLookups(): void {
    this.criteriaColumns = this.lookupService.getLookupValue(LOOKUP_KEYS.CRITERIA_COLUMNS);
    this.operatorOptions = this.withBetweenOperator(
      this.lookupService.getLookupValue(LOOKUP_KEYS.OPERATORS)
    );
    this.batchTypeOptions = this.lookupService.getLookupValue(LOOKUP_KEYS.BATCH_TYPES);
    this.dataTypeOptions = this.lookupService.getLookupValue(LOOKUP_KEYS.DATA_TYPES);
    this.providerTypeOptions = this.lookupService.getProviderLookupValue() ?? [];
  }

  /**
   * Adds Between to the operator list. Guarded so the option does not appear twice once the
   * lookup domain carries it and this can be dropped.
   */
  private withBetweenOperator(options: LookupValueItem[]): LookupValueItem[] {
    const existing = options ?? [];
    const alreadyThere = existing.some((option) => this.isBetween(option?.lkpValueCode));

    return alreadyThere
      ? existing
      : [...existing, { lkpValueCode: BETWEEN_OPERATOR_CODE, lkpValueName: 'Between' }];
  }

  /** True for the Between operator, compared case-insensitively against the stored code. */
  isBetween(operatorLkp: string | null | undefined): boolean {
    return String(operatorLkp ?? '').toLowerCase() === BETWEEN_OPERATOR_CODE.toLowerCase();
  }

  /** Whether this criteria row should show the second value input. */
  isBetweenRow(row: FormGroup): boolean {
    return this.isBetween(row.get('operatorLkp')?.value);
  }

  /** Display label for a criteria column, falling back to the stored code. */
  criteriaColumnLabel(columnLkp: string): string {
    return this.lookupLabel(this.criteriaColumns, columnLkp);
  }

  /** Display label for a criteria operator, falling back to the stored code. */
  operatorLabel(operatorLkp: string): string {
    return this.lookupLabel(this.operatorOptions, operatorLkp);
  }

  /**
   * Resolves a stored lkpValueCode to its lkpValueName, falling back to the raw
   * code so a value retired from the lookup still renders on an old batch.
   */
  private lookupLabel(options: LookupValueItem[], code: string): string {
    if (!code) return '';
    return this.lookupService.getLookupName(options, code) ?? code;
  }

  get criteriaFormArray(): FormArray<FormGroup> {
    return this.batchForm.get('criteria') as FormArray<FormGroup>;
  }

  /**
   * At least one criterion is required. Criteria are the batch's only scope, so
   * an empty list would sweep every eligible timesheet into one payroll batch.
   */
  get canSubmit(): boolean {
    return this.batchForm.valid && this.criteriaFormArray.length > 0 && !this.isSaving;
  }

  // ── Criteria rows ─────────────────────────────────────────────────────────

  addCriteriaRow(insertAfter?: number): void {
    const group = this.fb.group({
      columnLkp: ['', Validators.required],
      operatorLkp: ['', Validators.required],
      value: ['', Validators.required],
      // Only used by Between; its required rule is applied when that operator is picked.
      valueEnd: [''],
    });

    // A value typed for one column is meaningless for another.
    group.get('columnLkp')?.valueChanges.subscribe(() => {
      group.get('value')?.setValue('');
      group.get('valueEnd')?.setValue('');
    });

    // Between needs an upper bound; every other operator must not carry a stale one.
    group.get('operatorLkp')?.valueChanges.subscribe((operator) => {
      const valueEnd = group.get('valueEnd');
      if (!valueEnd) return;

      if (this.isBetween(operator)) {
        valueEnd.setValidators([Validators.required]);
      } else {
        valueEnd.setValidators([]);
        valueEnd.setValue('', { emitEvent: false });
      }

      valueEnd.updateValueAndValidity({ emitEvent: false });
    });

    if (typeof insertAfter === 'number' && insertAfter >= 0) {
      this.criteriaFormArray.insert(insertAfter + 1, group);
    } else {
      this.criteriaFormArray.push(group);
    }
  }

  /** Fixed value choices for the row's column, or null when the value is free text. */
  valueOptionsFor(row: FormGroup): { lkpValueName: string; lkpValueCode: string }[] | null {
    return FIXED_VALUE_OPTIONS[row.get('columnLkp')?.value] ?? null;
  }

  removeCriteriaRow(index: number): void {
    this.criteriaFormArray.removeAt(index);
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  onCancel(): void {
    this.goBack();
  }

  goBack(): void {
    this.router.navigate([NAVIGATE_BACK_ROUTE]);
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  onSave(): void {
    if (!this.programCode) {
      this.notificationService.error('Error', 'Program code is not available.');
      return;
    }
    if (this.batchForm.invalid) {
      this.batchForm.markAllAsTouched();
      return;
    }
    if (!this.criteriaFormArray.length) {
      this.notificationService.error('Error', 'Please add at least one criteria.');
      return;
    }

    this.isSaving = true;
    this.payrollService
      .createBatch({
        programCode: this.programCode,
        batchType: this.batchForm.get('batchType')?.value ?? '',
        dataType: this.batchForm.get('dataType')?.value ?? '',
        checkDate: this.formattedCheckDate,
        providerType: this.batchForm.get('providerType')?.value ?? '',
        criteria: this.buildCriteriaList(),
      })
      .pipe(finalize(() => { this.isSaving = false; }))
      .subscribe({
        next: (batchInstanceId) => {
          this.notificationService.success(
            'Success',
            batchInstanceId
              ? `Payroll batch ${batchInstanceId} created successfully`
              : 'Payroll batch created successfully'
          );
          this.goBack();
        },
        error: (err: unknown) => {
          console.error('Failed to create timesheet payroll batch:', err);
          this.notificationService.error('Error', 'Failed to create the timesheet payroll batch');
        },
      });
  }

  /**
   * The picked date as MM/dd/yyyy. Formatted from the Date's local components, so the calendar
   * day the user chose is the day that goes out — taking an ISO string off the Date instead
   * would shift it in any timezone behind UTC.
   */
  private get formattedCheckDate(): string {
    const value = this.batchForm.get('checkDate')?.value;
    return value instanceof Date ? this.dateTimeFormatter.formatDate(value, CHECK_DATE_FORMAT) : '';
  }

  private buildCriteriaList(): PayrollCriterion[] {
    return this.criteriaFormArray.controls.map((row) => {
      const criterion: PayrollCriterion = {
        columnLkp: row.get('columnLkp')?.value,
        operatorLkp: row.get('operatorLkp')?.value,
        value: row.get('value')?.value?.trim() ?? '',
      };

      // valueEnd only means anything for a range, so it is left off every other operator.
      if (this.isBetween(criterion.operatorLkp)) {
        criterion.valueEnd = row.get('valueEnd')?.value ?? '';
      }

      return criterion;
    });
  }
}
